import React, { useEffect, useState } from 'react';
import { Loader2, Sparkles, Heart, SendHorizontal } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { PublicFeedbackForm, FeedbackSubmissionResponse } from 'types';
import { apiService } from 'services/api';
import { cn } from 'lib/utils';

interface PublicFeedbackFormPageProps {
  externalId: string;
}

export const PublicFeedbackFormPage: React.FC<PublicFeedbackFormPageProps> = ({ externalId }) => {
  const [form, setForm] = useState<PublicFeedbackForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<FeedbackSubmissionResponse | null>(null);
  const [relationship, setRelationship] = useState('');
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    apiService
      .getPublicFeedbackForm(externalId)
      .then((data) => { if (active) setForm(data); })
      .catch((err) => {
        console.error('Failed to load feedback form', err);
        if (active) setError('We could not find this feedback form. The link may be incorrect or expired.');
      })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [externalId]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    if (!feedback.trim()) {
      setError('Please share a thoughtful note before submitting.');
      setMessage(null);
      return;
    }

    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      const transformed = await apiService.transformFeedbackForStudio({
        feedback: feedback.trim(),
        relationship: relationship || undefined,
        formTitle: form?.title,
      });

      const response = await apiService.submitFeedbackToForm(externalId, {
        insights: transformed.insights,
        actions: transformed.actions,
      });

      setMessage(response);
      if (!response.ignored) {
        setFeedback('');
        setRelationship('');
      }
    } catch (submissionError) {
      console.error('Failed to submit feedback', submissionError);
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : 'We were unable to send your feedback. Please try again in a moment.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center p-8 sm:p-16 bg-primary/10">
        <div className="w-full max-w-[720px] bg-card rounded-xl p-8 sm:p-14 border/35 shadow-lg flex flex-col gap-6 sm:gap-9">
          <div className="flex items-center justify-center gap-3 text-foreground-muted font-medium">
            <Loader2 size={24} className="animate-spin" /> Gathering the feedback page…
          </div>
        </div>
      </main>
    );
  }

  if (error && !form) {
    return (
      <main className="min-h-screen flex items-center justify-center p-8 sm:p-16 bg-primary/10">
        <div className="w-full max-w-[720px] bg-card rounded-xl p-8 sm:p-14 border/35 shadow-lg flex flex-col gap-6 sm:gap-9">
          <div className="rounded-xl p-6 border-red-400/35 bg-destructive/10 text-red-900 text-sm leading-relaxed">
            {error}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-8 sm:p-16 bg-primary/10">
      <div className="w-full max-w-[720px] bg-card rounded-xl p-8 sm:p-14 border/35 shadow-lg flex flex-col gap-6 sm:gap-9">
        <span className="self-start inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-primary/[0.17] text-foreground-strong">
          <Sparkles size={16} /> Thank you for sharing your perspective
        </span>

        <header className="flex flex-col gap-3">
          <h1 className="m-0 text-2xl font-bold tracking-tight leading-[1.2] text-foreground-strong flex items-center gap-3">
            <Heart size={32} className="text-red-500" />
            {form?.title || 'Share a reflection'}
          </h1>
          <p className="m-0 text-base sm:text-lg text-foreground-muted leading-[1.7]">
            Good feedback is specific, honest, and constructive—whether celebrating strengths or suggesting improvements. Everything is allowed. Don't hold back.
          </p>
        </header>

        <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
          <label htmlFor="feedback-relationship" className="flex flex-col gap-2.5 text-sm font-semibold text-foreground-strong">
            What's your relationship? (optional)
            <Input
              id="feedback-relationship"
              placeholder="e.g., colleague, friend, partner, family"
              value={relationship}
              onChange={(event) => setRelationship(event.target.value)}
              disabled={submitting}
            />
            <span className="text-xs text-foreground-muted font-normal">This helps provide context for your feedback.</span>
          </label>

          <label htmlFor="feedback-message" className="flex flex-col gap-2.5 text-sm font-semibold text-foreground-strong">
            Your feedback
            <Textarea
              id="feedback-message"
              placeholder="Share anything you want to say..."
              value={feedback}
              onChange={(event) => setFeedback(event.target.value)}
              disabled={submitting}
              className="min-h-[180px]"
            />
            <span className="text-xs text-foreground-muted font-normal">Your feedback will be processed and transformed into insights.</span>
          </label>

          {error && (
            <div className="rounded-xl p-6 border-red-400/35 bg-destructive/10 text-red-900 text-sm leading-relaxed" role="alert">
              {error}
            </div>
          )}
          {message && (
            <div
              className={cn(
                'rounded-xl p-6 border text-sm leading-relaxed',
                !message.ignored
                  ? 'border-green-500/35 bg-green-500/10 text-green-900'
                  : 'border-red-400/35 bg-destructive/10 text-red-900'
              )}
            >
              {message.message}
            </div>
          )}

          <Button type="submit" disabled={submitting} className="self-start rounded-xl px-7 py-4 h-auto text-base font-semibold">
            {submitting ? <Loader2 size={20} className="animate-spin" /> : <SendHorizontal size={20} />}
            {submitting ? 'Sending…' : 'Send Feedback'}
          </Button>
        </form>

        <p className="m-0 text-xs text-foreground-muted text-center">
          Your feedback is transformed by AI into insights and optional actions.
        </p>
      </div>
    </main>
  );
};
